import { type ReactNode } from "react";
import { motion } from "framer-motion";
import type { ReceiptExtraction, MatchResult } from "../types/receipt";

interface ReceiptResultsProps {
  extraction: ReceiptExtraction;
  match: MatchResult | null;
  onDismiss: () => void;
}

function formatDollars(amount: string | null): string {
  if (amount === null) return "—";
  const num = parseFloat(amount);
  if (isNaN(num)) return "—";
  return num.toLocaleString("en-US", { style: "currency", currency: "USD" });
}

export function ReceiptResults({ extraction, match, onDismiss }: ReceiptResultsProps): ReactNode {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      style={{
        border: "1px solid var(--color-border, #d1d5db)",
        borderRadius: "12px",
        padding: "20px",
        background: "var(--color-surface, #f9fafb)",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
        }}
      >
        <h3 style={{ margin: 0, fontSize: "1.1rem" }}>Receipt Details</h3>
        <button
          onClick={onDismiss}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            fontSize: "1.2rem",
            color: "var(--color-text-muted, #6b7280)",
          }}
          aria-label="Dismiss receipt results"
        >
          ✕
        </button>
      </div>

      {/* Header: vendor, date, total */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: "12px",
          marginBottom: "16px",
        }}
      >
        <div>
          <div
            style={{
              fontSize: "0.75rem",
              color: "var(--color-text-muted, #6b7280)",
              textTransform: "uppercase",
            }}
          >
            Vendor
          </div>
          <div style={{ fontWeight: 600, fontSize: "1rem" }}>
            {extraction.vendor_name ?? "Unknown vendor"}
          </div>
        </div>
        <div>
          <div
            style={{
              fontSize: "0.75rem",
              color: "var(--color-text-muted, #6b7280)",
              textTransform: "uppercase",
            }}
          >
            Date
          </div>
          <div style={{ fontWeight: 600, fontSize: "1rem" }}>
            {extraction.transaction_date ?? "Unknown date"}
          </div>
        </div>
        <div>
          <div
            style={{
              fontSize: "0.75rem",
              color: "var(--color-text-muted, #6b7280)",
              textTransform: "uppercase",
            }}
          >
            Total
          </div>
          <div style={{ fontWeight: 700, fontSize: "1.25rem" }}>
            {formatDollars(extraction.total_amount)}
          </div>
        </div>
        {extraction.payment_method && (
          <div>
            <div
              style={{
                fontSize: "0.75rem",
                color: "var(--color-text-muted, #6b7280)",
                textTransform: "uppercase",
              }}
            >
              Payment
            </div>
            <div style={{ fontSize: "1rem" }}>{extraction.payment_method}</div>
          </div>
        )}
      </div>

      {/* Line items */}
      {extraction.line_items.length > 0 && (
        <div style={{ marginBottom: "16px" }}>
          <div
            style={{
              fontSize: "0.75rem",
              color: "var(--color-text-muted, #6b7280)",
              textTransform: "uppercase",
              marginBottom: "8px",
            }}
          >
            Line Items
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
            <thead>
              <tr style={{ borderBottom: "1px solid var(--color-border, #d1d5db)" }}>
                <th style={{ textAlign: "left", padding: "4px 8px", fontWeight: 500 }}>Item</th>
                <th style={{ textAlign: "right", padding: "4px 8px", fontWeight: 500 }}>Qty</th>
                <th style={{ textAlign: "right", padding: "4px 8px", fontWeight: 500 }}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {extraction.line_items.map((item, index) => (
                <tr
                  key={index}
                  style={{ borderBottom: "1px solid var(--color-border-light, #e5e7eb)" }}
                >
                  <td style={{ padding: "6px 8px" }}>{item.description || "—"}</td>
                  <td style={{ textAlign: "right", padding: "6px 8px" }}>
                    {item.quantity !== null ? item.quantity : ""}
                  </td>
                  <td
                    style={{
                      textAlign: "right",
                      padding: "6px 8px",
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {formatDollars(item.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Tax and tips */}
      {(extraction.tax_amounts.length > 0 || extraction.tips !== null) && (
        <div style={{ marginBottom: "16px", fontSize: "0.9rem" }}>
          {extraction.subtotal_amount !== null && (
            <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 8px" }}>
              <span>Subtotal</span>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>
                {formatDollars(extraction.subtotal_amount)}
              </span>
            </div>
          )}
          {extraction.tax_amounts.map((tax, index) => (
            <div
              key={index}
              style={{ display: "flex", justifyContent: "space-between", padding: "4px 8px" }}
            >
              <span>
                {tax.tax_type || "Tax"}
                {tax.rate_if_visible ? ` (${tax.rate_if_visible})` : ""}
              </span>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>
                {formatDollars(tax.amount)}
              </span>
            </div>
          ))}
          {extraction.tips !== null && (
            <div style={{ display: "flex", justifyContent: "space-between", padding: "4px 8px" }}>
              <span>Tip</span>
              <span style={{ fontVariantNumeric: "tabular-nums" }}>
                {formatDollars(extraction.tips)}
              </span>
            </div>
          )}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              padding: "4px 8px",
              fontWeight: 700,
              borderTop: "2px solid var(--color-border, #d1d5db)",
              marginTop: "4px",
            }}
          >
            <span>Total</span>
            <span style={{ fontVariantNumeric: "tabular-nums" }}>
              {formatDollars(extraction.total_amount)}
            </span>
          </div>
        </div>
      )}

      {/* Match result — no confidence scores shown per charter */}
      <div
        style={{
          padding: "12px",
          borderRadius: "8px",
          background: match?.matched_transaction_id
            ? "var(--color-success-bg, #f0fdf4)"
            : "var(--color-warning-bg, #fffbeb)",
        }}
      >
        {match?.matched_transaction_id ? (
          <div style={{ fontWeight: 600 }}>Matched to bank charge</div>
        ) : (
          <div style={{ color: "var(--color-text-muted, #6b7280)" }}>
            We have your receipt. When the charge shows on your bank statement, we will look for a
            match.
          </div>
        )}
      </div>

      <button
        onClick={onDismiss}
        style={{
          marginTop: "16px",
          width: "100%",
          padding: "10px",
          border: "1px solid var(--color-border, #d1d5db)",
          borderRadius: "8px",
          background: "white",
          cursor: "pointer",
          fontSize: "0.9rem",
        }}
      >
        Upload another receipt
      </button>
    </motion.div>
  );
}
